import React, { useState, useRef, useCallback, useEffect } from 'react';
import { captureWithEnhancedCanvas } from './screenshotEngines';

interface Props {
    onCapture: (imageSrc: string) => void;
    onCancel: () => void;
    initialImage?: string; // Dùng khi cắt lại (recrop) từ ảnh đã chụp trên form feedback hoặc từ Native Tab Capture
}

/**
 * Camera Viewfinder Screen Capture (Kính ngắm máy ảnh)
 * 
 * Features:
 * 1. Zero UI Artifacts: Chụp ngầm trong bộ nhớ, không popup/spinner gây dính vào ảnh.
 * 2. Non-Destructive Post-Capture Crop (Cắt vùng sau khi chụp):
 *    - Click đơn: Chụp đóng băng khung hình ngay lập tức (Frozen review mode).
 *    - Sau khi click chụp: Người dùng có thể giữ kéo chuột trực tiếp trên ảnh để crop vùng cần lấy, 
 *      thay vì phải chụp lại từ đầu!
 *    - Hoặc bấm [✓ Dùng toàn màn hình] (phím Enter) để lấy full window.
 * 3. Hỗ trợ Recrop từ ảnh gốc trên Form Feedback thông qua prop `initialImage`.
 * 4. Camera Viewfinder Aesthetic:
 *    - Đường gióng chữ thập (Crosshair Guide Lines).
 *    - 4 góc vạch ngắm máy ảnh (Corner Brackets ⌜ ⌝ ⌞ ⌟).
 *    - Hiệu ứng chớp flash máy ảnh khi bấm máy (Shutter Flash Effect).
 */
export function RegionSelector({ onCapture, onCancel, initialImage }: Props) {
    const [phase, setPhase] = useState<'capturing' | 'viewfinder' | 'frozen_review'>(
        initialImage ? 'frozen_review' : 'capturing'
    );
    const [fullImage, setFullImage] = useState<string | null>(initialImage || null);
    const [selecting, setSelecting] = useState(false);
    const [start, setStart] = useState({ x: 0, y: 0 });
    const [end, setEnd] = useState({ x: 0, y: 0 });
    const [confirmedRect, setConfirmedRect] = useState<{ left: number; top: number; width: number; height: number } | null>(null);
    const [mousePos, setMousePos] = useState({ x: -100, y: -100 });
    const [isFlashActive, setIsFlashActive] = useState(false);

    const mouseDownPosRef = useRef({ x: 0, y: 0 });
    const isMouseDownRef = useRef(false);

    const handleShutterTrigger = useCallback((dataUrlToReturn: string) => {
        setIsFlashActive(true);
        // Chớp flash 100ms rồi đóng kính ngắm và trả về kết quả
        setTimeout(() => {
            onCapture(dataUrlToReturn);
        }, 100);
    }, [onCapture]);

    const performCrop = useCallback((cropRect: { left: number; top: number; width: number; height: number }, sourceImgUrl: string) => {
        const img = new Image();
        img.onload = () => {
            const scaleX = img.naturalWidth / window.innerWidth;
            const scaleY = img.naturalHeight / window.innerHeight;
            const cropCanvas = document.createElement('canvas');
            const cw = Math.round(cropRect.width * scaleX);
            const ch = Math.round(cropRect.height * scaleY);
            cropCanvas.width = Math.max(1, cw);
            cropCanvas.height = Math.max(1, ch);
            const ctx = cropCanvas.getContext('2d')!;
            ctx.drawImage(
                img,
                Math.round(cropRect.left * scaleX), Math.round(cropRect.top * scaleY), cw, ch,
                0, 0, cw, ch
            );
            handleShutterTrigger(cropCanvas.toDataURL('image/png'));
        };
        img.src = sourceImgUrl;
    }, [handleShutterTrigger]);

    // Phím tắt bàn phím (Enter để xác nhận, ESC để hủy)
    useEffect(() => {
        const handler = (e: KeyboardEvent) => { 
            if (e.key === 'Escape') {
                if (confirmedRect) {
                    setConfirmedRect(null); // Bỏ vùng crop hiện tại
                } else {
                    onCancel();
                }
            } else if (e.key === 'Enter') {
                if (phase === 'frozen_review' && fullImage) {
                    if (confirmedRect && confirmedRect.width > 5 && confirmedRect.height > 5) {
                        performCrop(confirmedRect, fullImage);
                    } else {
                        handleShutterTrigger(fullImage);
                    }
                }
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [onCancel, phase, fullImage, confirmedRect, performCrop, handleShutterTrigger]);

    // Silent Background Capture on mount (chỉ chạy nếu không có initialImage)
    useEffect(() => {
        if (initialImage) return;
        let isCancelled = false;

        const performSilentCapture = async () => {
            try {
                // Đợi 40ms để DOM unmount/ẩn triệt để feedback panel trước khi quét
                await new Promise(r => setTimeout(r, 40));
                if (isCancelled) return;

                const dataUrl = await captureWithEnhancedCanvas();
                if (isCancelled) return;

                // Load image into memory
                const img = new Image();
                img.onload = () => {
                    if (isCancelled) return;
                    setFullImage(dataUrl);
                    setPhase('viewfinder');
                };
                img.src = dataUrl;
            } catch (err) {
                console.error('[CameraViewfinder] Capture error:', err);
                if (!isCancelled) onCancel();
            }
        };

        performSilentCapture();

        return () => {
            isCancelled = true;
        };
    }, [onCancel, initialImage]);

    const onMouseDown = useCallback((e: React.MouseEvent) => {
        if (e.button !== 0) return; // Chỉ nhận chuột trái
        isMouseDownRef.current = true;
        mouseDownPosRef.current = { x: e.clientX, y: e.clientY };
        setStart({ x: e.clientX, y: e.clientY });
        setEnd({ x: e.clientX, y: e.clientY });
        setSelecting(true);
    }, []);

    const onMouseMove = useCallback((e: React.MouseEvent) => {
        setMousePos({ x: e.clientX, y: e.clientY });
        if (!selecting || !isMouseDownRef.current) return;
        setEnd({ x: e.clientX, y: e.clientY });
    }, [selecting]);

    const onMouseUp = useCallback((e: React.MouseEvent) => {
        if (!isMouseDownRef.current || !fullImage) return;
        isMouseDownRef.current = false;
        setSelecting(false);

        const currentX = e.clientX;
        const currentY = e.clientY;
        const deltaX = Math.abs(currentX - mouseDownPosRef.current.x);
        const deltaY = Math.abs(currentY - mouseDownPosRef.current.y);

        // KHI Ở GIAI ĐOẠN 1: Kính ngắm đang mở (viewfinder)
        if (phase === 'viewfinder') {
            // Nếu người dùng Click đơn (d <= 5px):
            // Chớp flash nhẹ, đóng băng khung hình và CHUYỂN SANG CHẾ ĐỘ CHO PHÉP KÉO CROP!
            if (deltaX <= 5 && deltaY <= 5) {
                setIsFlashActive(true);
                setTimeout(() => setIsFlashActive(false), 120);
                setPhase('frozen_review');
                return;
            }

            // Nếu người dùng giữ kéo chọn vùng ngay từ đầu:
            const w = Math.abs(currentX - start.x);
            const h = Math.abs(currentY - start.y);
            if (w >= 10 && h >= 10) {
                const rectToCrop = {
                    left: Math.min(start.x, currentX),
                    top: Math.min(start.y, currentY),
                    width: w,
                    height: h,
                };
                setConfirmedRect(rectToCrop);
                setPhase('frozen_review');
                return;
            }
        }

        // KHI Ở GIAI ĐOẠN 2: Đã click chụp xong (frozen_review)
        if (phase === 'frozen_review') {
            const w = Math.abs(currentX - start.x);
            const h = Math.abs(currentY - start.y);
            if (w >= 10 && h >= 10) {
                // Người dùng vừa kéo chọn một vùng crop mới trên ảnh đã chụp
                setConfirmedRect({
                    left: Math.min(start.x, currentX),
                    top: Math.min(start.y, currentY),
                    width: w,
                    height: h,
                });
            }
        }
    }, [fullImage, phase, start]);

    // Phase 1: Capturing — Silent mode, không render bất kỳ UI spinner nào để tránh dính vào ảnh!
    if (phase === 'capturing') {
        return null;
    }

    // Tính toán hình chữ nhật hiện tại (đang kéo hoặc đã xác nhận)
    const currentDragRect = {
        left: Math.min(start.x, end.x),
        top: Math.min(start.y, end.y),
        width: Math.abs(end.x - start.x),
        height: Math.abs(end.y - start.y),
    };

    const isDragging = selecting && currentDragRect.width > 5 && currentDragRect.height > 5;
    const activeRect = isDragging ? currentDragRect : confirmedRect;
    const hasSelection = !!activeRect && activeRect.width > 5 && activeRect.height > 5;

    return (
        <div
            className="camera-viewfinder-root fixed inset-0 z-[100] cursor-crosshair select-none overflow-hidden"
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
        >
            {/* Shutter Flash Effect */}
            {isFlashActive && (
                <div className="fixed inset-0 z-[120] bg-white pointer-events-none transition-opacity duration-100 opacity-90" />
            )}

            {/* Frozen Background Image (100% pixel-perfect) */}
            {fullImage && (
                <img
                    src={fullImage}
                    alt=""
                    className="absolute inset-0 w-full h-full pointer-events-none"
                    style={{ objectFit: 'fill' }}
                    draggable={false}
                />
            )}

            {/* Camera Viewfinder Dimmed Overlay with Cutout */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none z-10">
                <defs>
                    <mask id="viewfinder-cutout-mask">
                        <rect x="0" y="0" width="100%" height="100%" fill="white" />
                        {hasSelection && (
                            <rect
                                x={activeRect.left}
                                y={activeRect.top}
                                width={activeRect.width}
                                height={activeRect.height}
                                fill="black"
                            />
                        )}
                    </mask>
                </defs>
                <rect
                    x="0"
                    y="0"
                    width="100%" height="100%"
                    fill="rgba(0, 0, 0, 0.35)"
                    mask="url(#viewfinder-cutout-mask)"
                />
            </svg>

            {/* Crosshair Guidelines (Đường gióng chữ thập theo chuột - hiển thị khi chưa có vùng cố định) */}
            {!selecting && mousePos.x >= 0 && (
                <>
                    <div
                        className="absolute top-0 bottom-0 pointer-events-none z-10 w-[1px] bg-sky-400/40"
                        style={{ left: mousePos.x }}
                    />
                    <div
                        className="absolute left-0 right-0 pointer-events-none z-10 h-[1px] bg-sky-400/40"
                        style={{ top: mousePos.y }}
                    />
                </>
            )}

            {/* TOP INSTRUCTION & ACTION TOOLBAR */}
            {phase === 'viewfinder' ? (
                /* Giai đoạn 1: Kính ngắm chờ bấm máy */
                <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-slate-900/85 backdrop-blur-md text-white text-xs px-5 py-2.5 rounded-full pointer-events-none z-30 shadow-2xl border border-slate-700/60 flex items-center gap-3">
                    <span className="text-sm">📷</span>
                    <span className="font-semibold text-sky-400">Click chuột để chụp màn hình</span>
                    <span className="text-slate-400">•</span>
                    <span className="text-slate-200">Hoặc giữ kéo để chọn vùng ngay</span>
                    <span className="text-slate-400">•</span>
                    <span className="text-rose-300 font-mono text-[11px]">ESC để hủy</span>
                </div>
            ) : (
                /* Giai đoạn 2: ĐÃ CHỤP XONG KHOẢNH KHẮC - CHO PHÉP KÉO CROP HOẶC LẤY FULL */
                <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-slate-900/90 backdrop-blur-md text-white text-xs px-5 py-2.5 rounded-full z-40 shadow-2xl border border-sky-500/50 flex items-center gap-3 animate-in fade-in slide-in-from-top-2 duration-200">
                    <span className="flex items-center gap-1.5 text-emerald-400 font-semibold">
                        <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                        Đã chụp màn hình!
                    </span>
                    <span className="text-slate-500">|</span>
                    <span className="text-slate-200 font-medium">✂️ Giữ kéo chuột trên ảnh để crop vùng</span>
                    <span className="text-slate-500">|</span>
                    <button
                        type="button"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (fullImage) handleShutterTrigger(fullImage);
                        }}
                        className="px-3 py-1 bg-sky-600 hover:bg-sky-500 text-white font-medium rounded-full shadow transition-all flex items-center gap-1 cursor-pointer"
                    >
                        ✓ Dùng toàn màn hình <span className="text-[10px] opacity-80 font-mono">(Enter)</span>
                    </button>
                    <button
                        type="button"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onCancel();
                        }}
                        className="text-slate-400 hover:text-white px-2 py-0.5 rounded transition-colors cursor-pointer"
                    >
                        ✕ Hủy <span className="text-[10px] font-mono">(ESC)</span>
                    </button>
                </div>
            )}

            {/* SELECTION BOX, CORNER BRACKETS & ACTIONS */}
            {hasSelection && activeRect && (
                <>
                    {/* Main Selection Border with outer glow */}
                    <div
                        className="absolute border-2 border-sky-400 pointer-events-none z-20 shadow-[0_0_0_1px_rgba(255,255,255,0.7)]"
                        style={{
                            left: activeRect.left,
                            top: activeRect.top,
                            width: activeRect.width,
                            height: activeRect.height,
                        }}
                    />

                    {/* Camera Corner Brackets (Vạch góc máy ảnh ⌜ ⌝ ⌞ ⌟) */}
                    <div
                        className="absolute pointer-events-none z-30 w-3.5 h-3.5 border-t-2 border-l-2 border-white"
                        style={{ left: activeRect.left - 2, top: activeRect.top - 2 }}
                    />
                    <div
                        className="absolute pointer-events-none z-30 w-3.5 h-3.5 border-t-2 border-r-2 border-white"
                        style={{ left: activeRect.left + activeRect.width - 12, top: activeRect.top - 2 }}
                    />
                    <div
                        className="absolute pointer-events-none z-30 w-3.5 h-3.5 border-b-2 border-l-2 border-white"
                        style={{ left: activeRect.left - 2, top: activeRect.top + activeRect.height - 12 }}
                    />
                    <div
                        className="absolute pointer-events-none z-30 w-3.5 h-3.5 border-b-2 border-r-2 border-white"
                        style={{ left: activeRect.left + activeRect.width - 12, top: activeRect.top + activeRect.height - 12 }}
                    />

                    {/* Real-time Dimensions Badge */}
                    <div
                        className="absolute pointer-events-none z-30 text-[11px] font-mono text-white bg-sky-600/95 px-2 py-0.5 rounded shadow-lg border border-sky-400/50 flex items-center gap-1.5"
                        style={{
                            left: activeRect.left,
                            top: activeRect.top > 30 ? activeRect.top - 26 : activeRect.top + activeRect.height + 6,
                        }}
                    >
                        <span>📐 {Math.round(activeRect.width)} × {Math.round(activeRect.height)} px</span>
                    </div>

                    {/* CONFIRMATION POPUP BUTTONS (Hiển thị khi người dùng đã thả chuột chọn vùng) */}
                    {!selecting && confirmedRect && fullImage && (
                        <div
                            className="absolute pointer-events-auto z-40 flex items-center gap-1.5 bg-slate-900/95 backdrop-blur-md text-white p-1.5 rounded-lg shadow-2xl border border-sky-400/60 animate-in fade-in zoom-in-95 duration-150"
                            style={{
                                left: Math.max(10, Math.min(activeRect.left, window.innerWidth - 320)),
                                top: activeRect.top + activeRect.height + 10 > window.innerHeight - 50 
                                    ? Math.max(10, activeRect.top - 44) 
                                    : activeRect.top + activeRect.height + 10,
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    performCrop(confirmedRect, fullImage);
                                }}
                                className="px-3 py-1 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold rounded shadow flex items-center gap-1.5 transition-all cursor-pointer"
                            >
                                ✓ Cắt vùng này <span className="text-[10px] opacity-75 font-mono">(Enter)</span>
                            </button>
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setConfirmedRect(null);
                                }}
                                className="px-2.5 py-1 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs rounded transition-all flex items-center gap-1 cursor-pointer"
                            >
                                ↺ Chọn lại
                            </button>
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleShutterTrigger(fullImage);
                                }}
                                className="px-2.5 py-1 bg-sky-700 hover:bg-sky-600 text-white text-xs rounded transition-all flex items-center gap-1 cursor-pointer"
                            >
                                🖼️ Dùng toàn bộ
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default RegionSelector;
